/**
 * Pure click-resolution logic for the quote-document editor's selection model,
 * shared so it can be unit-tested without a DOM.
 *
 * The editor resolves a click in the rendered preview to the source block it
 * should select. Two regimes coexist in one document:
 *
 * - Flat regime: a click resolves to the nearest ancestor that carries a
 *   positional node id (`data-fp-node`), selecting that block.
 * - Template regime: a repeater (fp-lines and friends) stamps its per-item
 *   instances with `data-fp-instance-of="<owning node id>"`. A click inside a
 *   stamped instance resolves to the OWNING repeater's node id, so the whole
 *   repeater is selected as one opaque block and its interior is never an
 *   editing target.
 *
 * The DOM helper in the frontend walks real elements; this mirrors the same
 * decision over the click's ancestor chain (innermost first).
 */

/** One ancestor of a click target, as the resolver needs to see it. */
export interface SelectionAncestor {
  /** The element's `data-fp-node` value, or null when it carries none. */
  nodeId: string | null;
  /** The element's `data-fp-instance-of` value, or null when it carries none. */
  instanceOf: string | null;
}

/**
 * Resolves a click's ancestor chain to the source block node id it selects: the
 * owning repeater's id when the click sits inside a stamped instance, otherwise
 * the nearest ancestor's node id.
 * @param ancestors - The click target's ancestors, innermost first
 * @returns The selected block's node id, or null when none resolves
 */
export function resolveSelectedNodeId(
  ancestors: readonly SelectionAncestor[],
): string | null {
  const instanceOwner = ancestors.find(
    (ancestor) => ancestor.instanceOf !== null,
  )?.instanceOf;
  if (instanceOwner !== undefined && instanceOwner !== null) {
    return instanceOwner;
  }
  return ancestors.find((ancestor) => ancestor.nodeId !== null)?.nodeId ?? null;
}
